import { router } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { ActionSheet } from '@/src/elements/action'
import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import type { TListboxItemProps } from '@/src/elements/listbox'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import type {
  TCommunityApiHasJoinedInfos,
  TCommunityApiRoleInfos,
  TMediumCommunityApi,
} from '@/src/features/community/api/communityApi.types'
import { COMMUNITY_LIFETIME, EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import { useToggleFavoritePublication } from '@/src/features/publication/services/useFavoritePublication'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import type { TPermissionKey } from '@/src/general/role-access'
import { useMakeIsGranted } from '@/src/general/role-access/useMakeIsGranted'
import { useRoutes } from '@/src/general/utils/routes'

export type TPublicationUpdateProps = {
  publicationId: number
  isFavorite: boolean
  community?: Pick<TMediumCommunityApi, 'id' | 'slug' | 'lifetime'> &
    TCommunityApiRoleInfos &
    TCommunityApiHasJoinedInfos
  buttonSize?: TDefaultButtonProps['size']
  buttonColors?: TDefaultButtonProps['colors']
  buttonVariant?: TDefaultButtonProps['variant']
  testID?: string
}

// Define itemPermissions mapping testIDs to permission keys
const itemPermissions: { [key: string]: TPermissionKey } = {
  [TEST_ID.PUBLICATION.OPTIONS.MANAGE_CONTENT]: 'canManageContent',
  [TEST_ID.PUBLICATION.OPTIONS.NOTIFICATIONS]: 'canView',
  [TEST_ID.PUBLICATION.OPTIONS.VIEW_COMMUNITY]: 'canView',
  [TEST_ID.PUBLICATION.OPTIONS.FAVORITE]: 'canView',
  [TEST_ID.PUBLICATION.OPTIONS.LEAVE]: 'canView',
}

const createItem = ({
  onPress,
  message,
  icon,
  status = 'default',
  testID,
  disabled = false,
}: {
  onPress: () => void
  message: ReactNode
  icon: ICON
  status?: 'default' | 'danger'
  testID: string
  disabled?: boolean
}) =>
  disabled
    ? null
    : {
        onPress,
        children: <Typography colors={status}>{message}</Typography>,
        startContent: (
          <Icon
            name={icon}
            size="sm"
            className={status === 'danger' ? 'text-danger-text-contrast-default' : 'text-default-text-contrast-default'}
          />
        ),
        testID,
      }

export const PublicationUpdate = ({
  publicationId,
  isFavorite,
  community,
  buttonSize = 'md',
  buttonColors = 'default',
  buttonVariant = 'light',
  testID,
}: TPublicationUpdateProps): ReactNode => {
  const { routes } = useRoutes()
  const { isGranted } = useMakeIsGranted()
  const { mutate: toggleFavorite } = useToggleFavoritePublication()

  if (!community) return null

  const { id, hasJoined, lifetime, slug } = community
  const isEverlasting = lifetime === COMMUNITY_LIFETIME.EVERLASTING

  const listboxItems: ((TListboxItemProps & { testID: string }) | null)[] = [
    createItem({
      onPress: () => router.navigate(routes.community({ id, slug, origin: EVENT_ORIGIN.PUBLICATION })),
      message: <FormattedMessage id="community.toolbar.community" values={{ lifetime }} />,
      icon: ICON.EYE,
      testID: TEST_ID.PUBLICATION.OPTIONS.VIEW_COMMUNITY,
    }),
    createItem({
      onPress: () => toggleFavorite({ publicationId, action: isFavorite ? 'remove' : 'add' }),
      message: <FormattedMessage id={isFavorite ? 'publication.favorite.remove' : 'publication.favorite.add'} />,
      icon: isFavorite ? ICON.BOOKMARK_X : ICON.BOOKMARK,
      testID: TEST_ID.PUBLICATION.OPTIONS.FAVORITE,
      disabled: !getIsLoggedIn(),
    }),
    createItem({
      onPress: () => router.navigate(routes.communityNotifications({ id, slug })),
      message: <FormattedMessage id="word.notifications.mails" />,
      icon: ICON.BELL,
      testID: TEST_ID.PUBLICATION.OPTIONS.NOTIFICATIONS,
      disabled: !hasJoined,
    }),
    createItem({
      onPress: () => router.navigate(routes.leaveCommunity(`${id}-${slug}`)),
      message: (
        <FormattedMessage
          id={isEverlasting ? 'community.leave-community' : 'community.toolbar.stop'}
          values={{ lifetime }}
        />
      ),
      icon: isEverlasting ? ICON.LEAVE : ICON.ROAD_STOP,
      status: 'danger',
      testID: TEST_ID.PUBLICATION.OPTIONS.LEAVE,
      disabled: !hasJoined,
    }),
  ]

  /**
   * Filtered items to only include granted items
   *
   * Exemple if user has 'canManageContent' permission:
   * ```js
   * [
   *   {
   *     onPress: onPresses[0],
   *     children: <FormattedMessage id="publication.update" />,
   *     startContent: <Icon name={ICON.PENCIL} size="sm" />,
   *     testID: TEST_ID.PUBLICATION.OPTIONS.MANAGE_CONTENT,
   *   }
   * ]
   * ```
   *
   * Exemple if user has 'canView' permission:
   * ```js
   * [
   *   {
   *     onPress: onPresses[1],
   *     children: <FormattedMessage id="publication.goto" />,
   *     startContent: <Icon name={ICON.EYE} size="sm" />,
   *     testID: TEST_ID.PUBLICATION.OPTIONS.GOTO,
   *   }
   * ]
   * ```
   *
   * Exemple if user has both permissions:
   * ```js
   * [
   *   {
   *     onPress: onPresses[0],
   *     children: <FormattedMessage id="publication.update" />,
   *     startContent: <Icon name={ICON.PENCIL} size="sm" />,
   *     testID: TEST_ID.PUBLICATION.OPTIONS.MANAGE_CONTENT,
   *   },
   *   {
   *     onPress: onPresses[1],
   *     children: <FormattedMessage id="publication.goto" />,
   *     startContent: <Icon name={ICON.EYE} size="sm" />,
   *     testID: TEST_ID.PUBLICATION.OPTIONS.GOTO,
   *   }
   * ]
   * ```
   */
  const grantedItems = listboxItems.filter(
    (item): item is TListboxItemProps & { testID: string } =>
      !!item && isGranted(itemPermissions[item.testID], community),
  )

  return (
    <ActionSheet items={grantedItems}>
      <Button
        size={buttonSize}
        isIconOnly
        startIcon={ICON.ELLIPSIS_VERTICAL}
        colors={buttonColors}
        variant={buttonVariant}
        contrast={buttonVariant === 'light' ? 'low' : undefined}
        testID={testID}
      />
    </ActionSheet>
  )
}
